import MediaUploader from './components/MediaUploader';
import { DataTable } from './components/uploads/data-table';
import { getColumns } from './components/uploads/columns';
import { useRevalidator } from 'react-router';
import supabase from '@/utils/supabase';
import type { Database } from '@/types/database.types';
type Upload = Database['public']['Tables']['uploads']['Row'];
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type Row } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

const categoryLabels: Record<string, string> = {
  video: 'Videos',
  image: 'Images',
  audio: 'Audio',
};

export default function MediaTab({
  projectId,
  uploads,
}: {
  projectId: string;
  uploads: Upload[];
}) {
  const revalidator = useRevalidator();

  function renderMediaGroupHeaders(row: Row<Upload>) {
    const category = row.getValue('category') as string;
    return (
      <div className="flex items-center gap-2 font-semibold">
        {row.getIsExpanded() ? (
          <ChevronDown className="size-4" />
        ) : (
          <ChevronRight className="size-4" />
        )}
        {categoryLabels[category] || category}
        <span className="text-muted-foreground font-normal">
          ({row.subRows.length})
        </span>
      </div>
    );
  }

  const handleDelete = useCallback(
    async (uploadId: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      await fetch(`/api/engine/storage/${uploadId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      revalidator.revalidate();
    },
    [revalidator]
  );

  const columns = useMemo(() => getColumns(handleDelete), [handleDelete]);

  return (
    <>
      <MediaUploader
        projectId={projectId}
        onUploadComplete={() => revalidator.revalidate()}
      />
      <DataTable
        columns={columns}
        data={uploads}
        initialGrouping={['category']}
        renderGroupHeader={renderMediaGroupHeaders}
        emptyMessage="No files uploaded."
      />
    </>
  );
}
